import { Fragment } from 'react'
import { X, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { displayCitationPage } from '@/lib/citation-preview'
import type { ProposedQuestion } from '@/lib/api'

interface QuestionProposalRowsProps {
  questions: ProposedQuestion[]
  onRemove: (key: string) => void
}

/**
 * Extracted-question review rows, grouped under the schedule's own headings.
 * Question text is NOT editable here — verbatim by contract; rows can only
 * be removed. Hover the source icon for the verification quote.
 */
export function QuestionProposalRows({ questions, onRemove }: QuestionProposalRowsProps) {
  let lastGroup: string | null | undefined
  return (
    <div>
      {questions.map((question) => {
        const showGroup = question.groupTitle !== lastGroup
        lastGroup = question.groupTitle
        return (
          <Fragment key={question.key}>
            {showGroup && question.groupTitle && (
              <p className="border-t border-border/60 bg-muted/40 px-4 py-1.5 text-[11px] font-medium text-muted-foreground first:border-t-0">
                {question.groupTitle}
              </p>
            )}
            <div className="flex items-start gap-2 border-t border-border/60 px-4 py-2.5 first:border-t-0">
              {question.number && (
                <span className="mt-0.5 shrink-0 text-meta">
                  {question.number}
                </span>
              )}
              <p className="min-w-0 flex-1 text-sm leading-relaxed">{question.text}</p>
              <div className="flex shrink-0 items-center gap-1">
                {question.wordLimit != null && (
                  <span className="rounded border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">
                    {question.wordLimit.toLocaleString()} words
                  </span>
                )}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6" title="Source">
                      <Search className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="end">
                    <p className="mb-1 text-[11px] font-medium text-muted-foreground">
                      Verified against source
                      {displayCitationPage(question.sourcePage) != null &&
                        ` · p.${displayCitationPage(question.sourcePage)}`}
                    </p>
                    <p className="text-xs leading-relaxed">“{question.sourceQuote}”</p>
                  </PopoverContent>
                </Popover>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:text-destructive"
                  onClick={() => onRemove(question.key)}
                  title="Remove question"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </Fragment>
        )
      })}
    </div>
  )
}
